import { useCallback, useMemo, useState } from 'react';

import { TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerRenterContactWebApiHandler_getTenants } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useAuth } from '../../../../components/AuthProvider';
import EmptyTenantsCard from './components/EmptyTenantsCard';

import {
  CS_EVENT_TRIGGER_LOCATION,
  emitTenantsModuleAddTenantClickEvent,
  emitTenantsModuleSaveClickEvent,
  TENANTS_MODULE_ADD_TENANT_CLICK_EVENT_ID,
} from '../../../../analytics/events/tenantDirectory';
import { createZgApiHttpClient } from '../../../../clients/zgApiHttpClient';
import ConnectedModuleCard from '../../../ConnectedModuleCard';
import TenantAccordionList from './TenantAccordionList';

type Props = {
  listingAlias: string;
};

const TenantsModule = ({ listingAlias }: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const [isModalTenantSelectOpen, setIsModalTenantSelectOpen] = useState(false);

  const {
    data: getTenantsData,
    isFetching,
    isPending,
  } = useRentalManagerRenterContactWebApiHandler_getTenants({ listingAlias }, { enabled: Boolean(listingAlias) });

  const client = createZgApiHttpClient(process.env.NEXT_PUBLIC_ZG_API_PUBLIC ?? '');
  const tenants = getTenantsData?.tenants ?? [];
  const isEmptyTenants = tenants.length === 0;

  const invalidateGetTenantsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerRenterContactWebApiHandler_getTenants.getKey({ listingAlias }),
    });
  };

  const handleAddTenantClick = useCallback(() => {
    setIsModalTenantSelectOpen(true);
    if (user) {
      emitTenantsModuleAddTenantClickEvent({
        currentProperty: { listingAlias },
        user,
        triggerLocation: CS_EVENT_TRIGGER_LOCATION.ZRM_PROPERTY_OVERVIEW,
      });
    }
  }, [listingAlias, user]);

  const handleDeleteTenancyPeriodSuccess = () => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">Tenancy has been successfully removed</Toast>);
  };

  const handleDeleteTenantSuccess = () => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">The tenant has been successfully removed</Toast>);
  };

  const handleDeleteTenantError = ({ message }: { message: string }) => {
    enqueueToast(<Toast tone="critical">{message}</Toast>);
  };

  const handleEditTenantSuccess = () => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">Tenant details edited successfully</Toast>);
  };

  const handleLeaseTermActionSuccess = ({ isEditMode }: { isEditMode: boolean }) => {
    invalidateGetTenantsQuery();
    enqueueToast(
      <Toast tone="success">
        {isEditMode ? 'Tenancy has been successfully edited' : 'Tenancy has been successfully created'}
      </Toast>,
    );
  };

  const handleRequestProofSuccess = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">{message}</Toast>);
  };

  const handleRequestProofError = (message: string) => {
    enqueueToast(<Toast tone="critical">{message}</Toast>);
  };

  const renderCTA = useMemo(() => {
    return (
      <TextButton
        tone="brand"
        icon={<IconPlusCircleFilled />}
        data-eventid={TENANTS_MODULE_ADD_TENANT_CLICK_EVENT_ID}
        onClick={handleAddTenantClick}
      >
        Add tenant
      </TextButton>
    );
  }, [handleAddTenantClick]);

  return (
    <>
      <ConnectedModuleCard
        data-testid="TenantsModule"
        title="Tenants"
        ctaComponent={!isPending && renderCTA}
        ctaFontType="body-sm"
        justifyHeadingContent={'center'}
        isLoading={isPending || isFetching}
      >
        {isPending === false && isFetching === false && (
          <>
            {isEmptyTenants ? (
              <EmptyTenantsCard />
            ) : (
              <TenantAccordionList
                listingAlias={listingAlias}
                tenants={tenants}
                onDeleteTenancyPeriodSuccess={handleDeleteTenancyPeriodSuccess}
                onDeleteTenantSuccess={handleDeleteTenantSuccess}
                onDeleteTenantError={handleDeleteTenantError}
                onEditTenantSuccess={handleEditTenantSuccess}
                onLeaseTermActionSuccess={handleLeaseTermActionSuccess}
                onRequestProofSuccess={handleRequestProofSuccess}
                onRequestProofError={handleRequestProofError}
              />
            )}
          </>
        )}
      </ConnectedModuleCard>

      <ModalTenantSelect
        client={client}
        listingAlias={listingAlias}
        isOpen={isModalTenantSelectOpen}
        onAddRenterSuccess={() => {
          if (user) {
            emitTenantsModuleSaveClickEvent({
              triggerLocation: CS_EVENT_TRIGGER_LOCATION.ZRM_PROPERTY_OVERVIEW,
              currentProperty: { listingAlias },
              user,
            });
          }
        }}
        queryClient={queryClient}
        onClose={() => setIsModalTenantSelectOpen(false)}
        onPrimaryCTAClick={() => {
          invalidateGetTenantsQuery();
          setIsModalTenantSelectOpen(false);
        }}
      />
    </>
  );
};

export default TenantsModule;
